// ─────────────────────────────────────────────────────────────
// LukaDuka - Local Capture Queue (ver6)
// Strictly offline queue. NOT used when the device is online.
//
// Flow:
//   1. Device offline (or server unreachable) -> QuickCaptureSheet calls
//      queueOfflineCapture(). The capture is stored in IndexedDB with its
//      own unique ID and the local date it was captured on.
//   2. It appears in the merged Letterbox for the person who captured it
//      (and ONLY that person).
//   3. The queue sends it as soon as it can. It tries:
//        - when the app opens / sign-in is restored
//        - when the app comes back on screen
//        - when the phone reconnects
//        - when the server becomes reachable again (requestSync)
//        - on a real timer: 30s / 2m / 10m / then hourly
//   4. Each capture is sent via captureEntryWithResult() with its unique
//      client_capture_id. If it already arrived on an earlier attempt, the
//      database refuses a second copy and that counts as success.
//   5. On success -> the local copy is removed (it now lives on the server).
//   6. On failure -> the real reason is saved, the next retry is scheduled.
//      After 24h it is marked 'failed' (= needs attention) but it is STILL
//      retried hourly. A capture is never given up on.
//
// ver6 - 2 November 2026 (Offline Capture screen - Layer 3):
//   - ADDED: countQueuedCaptures(userId) - READ-ONLY count for the Offline
//     Capture screen. Reads the phone's queue directly; never asks Supabase
//     who is signed in (verified 2 Nov: that stalls ~25s offline with an
//     old login, then answers "nobody"). Sealed Safespace captures are
//     never counted (Decision G).
//   - CHANGED: getPendingCaptures(userId?) - when the caller passes the
//     user ID it already knows, that is used instead of asking Supabase.
//     Fixes waiting cards vanishing from the Letterbox when the app has
//     been open offline for over an hour (design brief section 4.7).
//     Sending is unchanged: it still requires the real login, checked by
//     the server.
//
// ver5 - 29 October 2026 (Offline capture sync fix - Layer 4b):
//   - queueOfflineCapture() takes an optional existing clientCaptureId.
//     QuickCaptureSheet passes it when an ONLINE save fails and the
//     capture falls back to this queue. Same ID = if the online save
//     actually reached the server, the retry is refused as a duplicate
//     and counted as success. No double entry, nothing lost.
//   - IDs now come from the shared generator in ./capture (newCaptureId),
//     replacing this file's private copy.
//
// ver4 - 29 October 2026 (Offline capture sync fix - Layer 4):
//   - ADDED: listens for "server reachable again" on the shared connection
//     noticeboard (src/shared/events/connectivityEvents.ts), emitted by
//     useOnlineStatus. Covers weak signal / captive-portal wifi, where the
//     phone never goes "offline" so no 'online' event ever fires.
//   - No other changes.
//
// ver3 - 29 October 2026 (Offline capture sync fix - Layer 3):
//   - FIX: sync previously only ran on the browser 'online' event, so a
//     capture made offline and reopened with signal never synced (the
//     Australia incident). Sync triggers now live here, in one place.
//   - FIX: the retry schedule was recorded but nothing ran it. A real
//     timer now wakes at the next due retry.
//   - FIX: captures were parked as 'failed' after 24h and skipped forever.
//     'failed' now means "needs attention" - still retried hourly.
//   - FIX: the real error was discarded. lastError now stores code,
//     message and time for the Letterbox to show.
//   - FIX (Walls Up): the Letterbox showed every queued capture on the
//     device, whoever captured it. getPendingCaptures() and sync now only
//     touch the signed-in person's captures.
//   - FIX: the fallback local ID was not a valid UUID. All new IDs are
//     real v4 UUIDs (localId === clientCaptureId for new captures).
//   - ADDED: clientCaptureId (duplicate protection, migration
//     lukaduka-migration-offline-capture-sync-ver1) and entryDate (local
//     date locked at capture, so travel cannot shift it).
//   - ADDED: older stored captures are upgraded automatically on read.
//   - ADDED: retryPendingCapture(localId) for the Letterbox "Try again".
//   - ADDED: requestSync(reason) for useOnlineStatus (Layer 4).
//   - ADDED: single-flight guard - overlapping triggers cannot double-send.
//   - REMOVED: removePendingCapture(). It destroyed an unsent capture,
//     which breaks "Nothing is Deleted" and the Letterbox decision L1
//     (24 Sep 2026). It had no caller. Do not reintroduce.
//   - Now uses captureEntryWithResult() instead of captureEntry().
//
// ver2 - May 2026:
//   - Removed optimisticCapture(); removed silent IndexedDB failure
//     swallowing; added retry fields, 24h give-up (now reversed in ver3),
//     subscribeToQueueChanges() and queue-change notifications.
//
// Uses raw IndexedDB (no library dependency).
// Path: src/modules/timeline/services/localCaptureQueue.ts
// Plan: lukaduka-offline-sync-fix-plan-29102026-ver2.md
// ─────────────────────────────────────────────────────────────

import { supabase } from '@/shared/services/supabase'
import { todayLocalISO, toLocalISODate } from '@/shared/utils/localDate'
import { onServerReachableAgain } from '@/shared/events/connectivityEvents'
import { captureEntryWithResult, newCaptureId, isValidCaptureId } from './capture'
import type { CaptureInput } from '../types/timeline.types'

const DB_NAME = 'lukaduka-local'
const DB_VERSION = 1
const STORE_NAME = 'capture_queue'

// ─── Types ───────────────────────────────────────────────────

/**
 * 'pending' = waiting to send / retrying.
 * 'failed'  = has been failing for 24h or more. Still retried hourly -
 *             the Letterbox shows a calm "needs attention" state with
 *             Try again and Copy text. Nothing is ever given up on.
 */
export type QueueStatus = 'pending' | 'failed'

export interface CaptureSyncError {
  /** Postgres / PostgREST code, or 'NETWORK' / 'UNKNOWN'. */
  code: string
  /** Reason, trimmed for storage. Never contains the capture text. */
  message: string
  /** UTC ISO of the failed attempt. */
  at: string
}

export interface LocalCapture {
  /** Local UUID - IDB key and the localId field on merged Letterbox items */
  localId: string
  /** Who captured it. Frozen at capture. Only this person's session sends it. */
  userId: string
  /** Same shape used by the capture service - one schema everywhere */
  input: CaptureInput
  toSafespace: boolean
  status: QueueStatus
  /** UTC ISO. Set once at capture time. Never mutated. */
  createdAt: string
  /** Unique ID sent to the server so a retry can never create a duplicate. */
  clientCaptureId: string
  /** Local calendar date (YYYY-MM-DD) where the moment was captured. */
  entryDate: string
  retryCount: number
  /** UTC ISO. Earliest moment this entry should be retried again. */
  nextRetryAt: string
  /** UTC ISO. When the very first sync failure happened. */
  firstFailedAt: string | null
  /** The most recent failure reason, or null if never failed. */
  lastError: CaptureSyncError | null
}

/**
 * Shape as it may exist in IndexedDB. Captures saved by ver2 lack the
 * ver3 fields; they are upgraded on read (see normaliseCapture).
 */
type StoredCapture = Omit<LocalCapture, 'clientCaptureId' | 'entryDate' | 'lastError'> & {
  clientCaptureId?: string
  entryDate?: string
  lastError?: CaptureSyncError | null
}

// ─── IDs ─────────────────────────────────────────────────────
// ver5: generated by the shared ./capture/newCaptureId (always a valid
// v4 UUID - the database column is type uuid).

// ─── IndexedDB connection (singleton) ────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'localId' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      dbPromise = null
      reject(request.error ?? new Error('IndexedDB open failed'))
    }
  })

  return dbPromise
}

// ─── Pub/sub for queue changes ───────────────────────────────

type QueueChangeListener = () => void
const listeners = new Set<QueueChangeListener>()

/**
 * Subscribe to queue change events. Fires after every mutating operation:
 *   - queueOfflineCapture (add)
 *   - retryPendingCapture (rescheduled)
 *   - sync (per-item success removal OR retry-state update)
 *   - automatic upgrade of older stored captures
 *
 * Returns an unsubscribe function.
 *
 * Used by useTimeline to refetch the merged Letterbox automatically.
 */
export function subscribeToQueueChanges(callback: QueueChangeListener): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

function notifyQueueChange(): void {
  for (const listener of listeners) {
    try {
      listener()
    } catch (err) {
      console.error('[localCaptureQueue] listener error:', err)
    }
  }
}

// ─── IndexedDB helpers (internal) ────────────────────────────

async function putCapture(capture: LocalCapture): Promise<void> {
  const db = await getDB()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(capture)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB put failed'))
  })
}

/** Internal only: removes the LOCAL copy after the server has it. */
async function removeSyncedCapture(localId: string): Promise<void> {
  const db = await getDB()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(localId)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed'))
  })
}

async function getAllStored(): Promise<StoredCapture[]> {
  const db = await getDB()
  return new Promise<StoredCapture[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const request = tx.objectStore(STORE_NAME).getAll()
    request.onsuccess = () => resolve(request.result as StoredCapture[])
    request.onerror = () => reject(request.error ?? new Error('IndexedDB getAll failed'))
  })
}

// Upgrade a capture saved by an older version so it has every ver3 field.
function normaliseCapture(stored: StoredCapture): { capture: LocalCapture; changed: boolean } {
  let changed = false

  let clientCaptureId = stored.clientCaptureId
  if (!isValidCaptureId(clientCaptureId)) {
    clientCaptureId = isValidCaptureId(stored.localId) ? stored.localId : newCaptureId()
    changed = true
  }

  let entryDate = stored.entryDate
  if (!entryDate) {
    // Best available for older captures: the capture time in the device's
    // current timezone. New captures lock this at capture instead.
    entryDate = toLocalISODate(stored.createdAt)
    changed = true
  }

  let lastError = stored.lastError
  if (lastError === undefined) {
    lastError = null
    changed = true
  }

  return {
    capture: { ...stored, clientCaptureId, entryDate, lastError },
    changed,
  }
}

/** Read every stored capture, upgrading (and saving) older ones. */
async function loadCaptures(): Promise<LocalCapture[]> {
  const stored = await getAllStored()
  const result: LocalCapture[] = []
  let anyChanged = false

  for (const item of stored) {
    const { capture, changed } = normaliseCapture(item)
    if (changed) {
      try {
        await putCapture(capture)
        anyChanged = true
      } catch (err) {
        console.error('[localCaptureQueue] Cannot upgrade stored capture:', err)
      }
    }
    result.push(capture)
  }

  if (anyChanged) notifyQueueChange()
  return result
}

async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession()
    return data.session?.user?.id ?? null
  } catch (err) {
    console.error('[localCaptureQueue] Cannot read session:', err)
    return null
  }
}

// ─── Public API: queue operations ────────────────────────────

/**
 * Write a capture to the offline queue.
 *
 * THROWS if the IndexedDB write fails. The caller MUST surface that failure
 * to the user. We never silently swallow IDB errors - that was the root
 * cause of "Captured" appearing for entries that were never saved.
 *
 * existingCaptureId (ver5): pass the ID of an ONLINE save that failed, so
 * the queued retry can never create a second copy if that save actually
 * reached the server. Omit for a normal offline capture.
 */
export async function queueOfflineCapture(
  userId: string,
  input: CaptureInput,
  toSafespace: boolean,
  existingCaptureId?: string
): Promise<LocalCapture> {
  const nowIso = new Date().toISOString()
  const id = isValidCaptureId(existingCaptureId) ? existingCaptureId : newCaptureId()

  const capture: LocalCapture = {
    localId: id,
    userId,
    input,
    toSafespace,
    status: 'pending',
    createdAt: nowIso,
    clientCaptureId: id,
    entryDate: todayLocalISO(), // locked HERE, where the moment happened
    retryCount: 0,
    nextRetryAt: nowIso, // eligible immediately
    firstFailedAt: null,
    lastError: null,
  }

  await putCapture(capture)
  notifyQueueChange()
  void armRetryTimer()
  return capture
}

/**
 * Return the SIGNED-IN person's queued captures, newest-first.
 * Includes 'pending' and 'failed' (needs attention).
 * Other people's captures on a shared device are never returned (Walls Up).
 * Used by useTimeline to merge into the Letterbox display.
 */
export async function getPendingCaptures(knownUserId?: string): Promise<LocalCapture[]> {
  // ver6: use the caller's user ID when given (no server call, so this works
  // offline with an old login). Otherwise fall back to the session.
  const userId = knownUserId ?? await getCurrentUserId()
  if (!userId) return []
  const all = await loadCaptures()
  return all
    .filter((c) => c.userId === userId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/**
 * ver6: how many captures are waiting on this phone for this person,
 * NOT counting sealed Safespace captures (Decision G - a private moment
 * leaves no visible trace). Read-only: never upgrades or rewrites stored
 * items, never touches the network. Returns 0 if the queue can't be read.
 * Used by the Offline Capture screen.
 */
export async function countQueuedCaptures(userId: string): Promise<number> {
  try {
    const all = await getAllStored()
    return all.filter((c) => c.userId === userId && !c.toSafespace).length
  } catch (err) {
    console.error('[localCaptureQueue] Cannot count queue:', err)
    return 0
  }
}

/**
 * "Try again" from the Letterbox: make this capture due now and send.
 * Only works on the signed-in person's own captures.
 * Resolves true if the capture was found and a send was started.
 */
export async function retryPendingCapture(localId: string): Promise<boolean> {
  const userId = await getCurrentUserId()
  if (!userId) return false

  const all = await loadCaptures()
  const capture = all.find((c) => c.localId === localId && c.userId === userId)
  if (!capture) return false

  await putCapture({ ...capture, nextRetryAt: new Date().toISOString() })
  notifyQueueChange()
  requestSync('manual-retry')
  return true
}

// ─── Retry scheduling ────────────────────────────────────────

const RETRY_INTERVALS_MS = [
  30 * 1_000,        // after attempt 1 -> wait 30s
  2 * 60 * 1_000,    // after attempt 2 -> wait 2m
  10 * 60 * 1_000,   // after attempt 3 -> wait 10m
]
const HOURLY_RETRY_MS = 60 * 60 * 1_000
/** After this long failing, show "needs attention". Retries continue. */
const NEEDS_ATTENTION_AFTER_MS = 24 * 60 * 60 * 1_000
/** The timer never fires more often than this. */
const MIN_TIMER_MS = 30 * 1_000
const MAX_ERROR_MESSAGE_LENGTH = 300

function nextRetryDelayMs(newRetryCount: number): number {
  // newRetryCount is the count AFTER incrementing; array is zero-indexed
  const idx = newRetryCount - 1
  if (idx >= 0 && idx < RETRY_INTERVALS_MS.length) {
    return RETRY_INTERVALS_MS[idx]
  }
  return HOURLY_RETRY_MS
}

// ─── Sync ────────────────────────────────────────────────────

// One pass over the signed-in person's due captures, oldest-first.
async function processQueueOnce(): Promise<number> {
  // navigator.onLine === false is reliable ("definitely offline").
  // true is not reliable, so we still attempt and handle failure.
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 0

  const userId = await getCurrentUserId()
  if (!userId) return 0 // not signed in - nothing can be sent safely

  let all: LocalCapture[]
  try {
    all = await loadCaptures()
  } catch (err) {
    console.error('[localCaptureQueue] Cannot read queue:', err)
    return 0
  }

  // Oldest-first - the Timeline should receive moments in the order they happened.
  // Only this person's captures (Walls Up).
  const mine = all
    .filter((c) => c.userId === userId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))

  let synced = 0

  for (const capture of mine) {
    const nowMs = Date.now()
    if (new Date(capture.nextRetryAt).getTime() > nowMs) continue

    const result = await captureEntryWithResult({
      userId: capture.userId,
      input: capture.input,
      clientCaptureId: capture.clientCaptureId,
      toSafespace: capture.toSafespace,
      entryDate: capture.entryDate,
      capturedAtIso: capture.createdAt,
    })

    if (result.ok) {
      try {
        await removeSyncedCapture(capture.localId)
        synced += 1
        notifyQueueChange()
      } catch (err) {
        // The server has it. If the local copy can't be removed now, the
        // next pass resends; the database refuses the duplicate and that
        // counts as success, so this is self-healing.
        console.error('[localCaptureQueue] Synced but cannot clear local copy:', err)
      }
      continue
    }

    // Failed - record why, schedule the next attempt. Never give up.
    const firstFailedAt = capture.firstFailedAt ?? new Date(nowMs).toISOString()
    const failingForMs = nowMs - new Date(firstFailedAt).getTime()
    const newRetryCount = capture.retryCount + 1

    const updated: LocalCapture = {
      ...capture,
      retryCount: newRetryCount,
      firstFailedAt,
      nextRetryAt: new Date(nowMs + nextRetryDelayMs(newRetryCount)).toISOString(),
      status: failingForMs >= NEEDS_ATTENTION_AFTER_MS ? 'failed' : 'pending',
      lastError: {
        code: result.errorCode,
        message: result.errorMessage.slice(0, MAX_ERROR_MESSAGE_LENGTH),
        at: new Date(nowMs).toISOString(),
      },
    }

    try {
      await putCapture(updated)
      notifyQueueChange()
    } catch (writeErr) {
      console.error('[localCaptureQueue] Cannot update retry state:', writeErr)
    }
  }

  return synced
}

let syncInFlight: Promise<number> | null = null
let rerunRequested = false

/**
 * Send every due capture for the signed-in person. Returns count synced.
 * Single-flight: if a sync is already running, this joins it and asks
 * for one more pass afterwards, so overlapping triggers cannot double-send.
 */
export function syncPendingCaptures(): Promise<number> {
  if (syncInFlight) {
    rerunRequested = true
    return syncInFlight
  }

  syncInFlight = (async () => {
    let total = 0
    try {
      do {
        rerunRequested = false
        total += await processQueueOnce()
      } while (rerunRequested)
    } catch (err) {
      console.error('[localCaptureQueue] Sync pass failed:', err)
    } finally {
      syncInFlight = null
      void armRetryTimer()
    }
    if (total > 0) {
      console.log(`[localCaptureQueue] Synced ${total} queued capture(s)`)
    }
    return total
  })()

  return syncInFlight
}

/**
 * Ask the queue to try now. Safe to call from anywhere, any number of
 * times. Used by the triggers below and by useOnlineStatus when the
 * server becomes reachable again (Layer 4).
 */
export function requestSync(_reason: string): void {
  void syncPendingCaptures()
}

// ─── Retry timer ─────────────────────────────────────────────

let retryTimer: ReturnType<typeof setTimeout> | null = null

// Arm one timer for the next due retry of the signed-in person's captures.
async function armRetryTimer(): Promise<void> {
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
  if (typeof window === 'undefined') return
  // Definitely offline: the 'online' event will wake us instead.
  if (typeof navigator !== 'undefined' && !navigator.onLine) return

  const userId = await getCurrentUserId()
  if (!userId) return

  let mine: LocalCapture[]
  try {
    mine = (await loadCaptures()).filter((c) => c.userId === userId)
  } catch {
    return
  }
  if (mine.length === 0) return

  const earliest = Math.min(...mine.map((c) => new Date(c.nextRetryAt).getTime()))
  const delay = Math.min(Math.max(earliest - Date.now(), MIN_TIMER_MS), HOURLY_RETRY_MS)

  retryTimer = setTimeout(() => {
    retryTimer = null
    requestSync('timer')
  }, delay)
}

// ─── Sync triggers (all in one place) ────────────────────────

let triggersInstalled = false

function installSyncTriggers(): void {
  if (triggersInstalled || typeof window === 'undefined') return
  triggersInstalled = true

  // Phone reconnects.
  window.addEventListener('online', () => requestSync('online'))

  // Server reachable again (weak signal / captive portal - the phone
  // never went "offline", so no 'online' event). ver4.
  onServerReachableAgain(() => requestSync('server-reachable'))

  // App comes back on screen.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') requestSync('foreground')
  })

  // App opens / sign-in restored / token refreshed.
  // Never await inside onAuthStateChange (supabase-js deadlock) -
  // defer with setTimeout(0).
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      setTimeout(() => requestSync(`auth:${event}`), 0)
    }
  })
}

installSyncTriggers()
